import { useEffect, useRef, useState } from 'react'

/**
 * How long a query gets to resolve before its skeleton is allowed on screen.
 *
 * 180ms. Chosen against what the number has to beat: on a warm local API the support
 * queries answer in ~30ms, so without a delay a skeleton mounts, paints one or two
 * frames, and unmounts — the "flash" this exists to remove. It is not layout shift;
 * the skeletons are footprint-exact (measured zero delta on every region), so nothing
 * moves. What the eye catches is the appearance and disappearance itself.
 *
 * Long enough to swallow a fast response, short enough that a genuinely slow one still
 * gets an acknowledgement inside the ~200-300ms window where a UI still reads as
 * "responding" rather than "stuck".
 */
export const DEFERRED_LOADING_DELAY_MS = 180

/**
 * How long the skeleton stays once it HAS been shown, even if the data lands immediately after.
 *
 * The delay alone is not enough, and the failure mode is specific: a response arriving at
 * 190ms clears the delay by 10ms, so the skeleton mounts and is torn down a frame later —
 * the same blink, just moved. A floor on the visible duration is what closes that window.
 *
 * 320ms is a little under two of the 180ms delays; the pair is chosen so that any response
 * either misses the skeleton entirely or gets a display long enough to read as deliberate.
 */
export const DEFERRED_LOADING_MIN_DISPLAY_MS = 320

/**
 * How long the loaded content takes to fade in.
 *
 * Legitimate here ONLY because the skeleton and the loaded content occupy identical
 * footprints — this is polish on a stable layout, not a mask for movement. Strictly
 * OPACITY: no transform, no height or margin animation, either of which would put back
 * exactly the shift the footprint audit removed.
 */
export const DEFERRED_LOADING_FADE_MS = 120

interface UseDeferredLoadingOptions {
  /** Grace period before the skeleton is allowed to mount. */
  delay?: number
  /** Floor on how long it stays once it has. */
  minDisplay?: number
}

/**
 * Should a loading skeleton be on screen right now?
 *
 * Wraps a query's `isLoading` in a delay-then-hold so a fast response never produces a
 * visible blink. Shared rather than repeated per page (§36): the two timings are one
 * decision, and the timer bookkeeping — which timer is live in which state, and clearing
 * it on unmount — is exactly the part that gets copied wrong.
 *
 * ## The contract
 *
 * | `isLoading` goes | within | result |
 * |---|---|---|
 * | true → false | < `delay` | never returns true — the skeleton is never mounted |
 * | true → false | > `delay` | true for at least `minDisplay` from when it first went true |
 * | false at mount | — | false immediately (cached data: no delay, no flash) |
 * | true → false → true | any | the pending timer is dropped, not left to fire late |
 *
 * ## Using it
 *
 * The caller gets a THIRD state, and has to render it deliberately:
 *
 * ```tsx
 * const showSkeleton = useDeferredLoading(isLoading)
 * ...
 * {showSkeleton ? <Skeleton /> : isLoading ? null : <Content />}
 * ```
 *
 * The `isLoading ? null` arm is not optional. Falling straight through to the loaded
 * branch during the grace period renders the page's EMPTY state over data that is still
 * in flight — "No requests yet" on a list that is about to have fourteen — which is a
 * worse artifact than the flash. Rendering nothing for <180ms is imperceptible; rendering
 * the wrong answer is not.
 */
export const useDeferredLoading = (
  isLoading: boolean,
  {
    delay = DEFERRED_LOADING_DELAY_MS,
    minDisplay = DEFERRED_LOADING_MIN_DISPLAY_MS,
  }: UseDeferredLoadingOptions = {},
): boolean => {
  const [visible, setVisible] = useState(false)

  /**
   * When the skeleton went up, for measuring `minDisplay` against.
   *
   * Set inside the show timer rather than at the start of the delay, so the floor covers
   * the time it is actually ON SCREEN. Measuring from the start of the delay would let a
   * response landing at `delay + 1` hide it 179ms early — reintroducing the blink the
   * floor exists to prevent.
   */
  const shownAtRef = useRef(0)

  useEffect(() => {
    if (isLoading) {
      // Already up. Do NOT restart the clock: a query that flickers back to loading (a
      // refetch, a scope change) while the skeleton is still showing should extend one
      // continuous display, not begin a second one that outlives it by another minDisplay.
      if (visible) return

      const timer = setTimeout(() => {
        shownAtRef.current = Date.now()
        setVisible(true)
      }, delay)
      // Cleanup covers three cases with one line: unmount, `isLoading` flipping back to
      // false inside the grace period (the fast-response case — the timer is dropped and
      // nothing is ever shown), and a true → false → true bounce, which lands here before
      // the next run schedules a fresh timer. A `setTimeout` surviving unmount would call
      // `setVisible` on a dead component.
      return () => clearTimeout(timer)
    }

    // Resolved inside the grace period. Nothing was mounted, so there is nothing to hold.
    if (!visible) return

    const remaining = minDisplay - (Date.now() - shownAtRef.current)
    if (remaining <= 0) {
      // The floor is already satisfied — hand over on this render rather than scheduling a
      // zero-length timeout, which would cost a frame for nothing.
      setVisible(false)
      return
    }

    const timer = setTimeout(() => setVisible(false), remaining)
    return () => clearTimeout(timer)
    // `visible` is a dependency on purpose: it is what re-runs this effect when the show
    // timer fires, so the hide path is evaluated against the state the timer just set
    // rather than a stale closure. Mirroring it into a ref instead would work and would be
    // harder to read.
  }, [isLoading, visible, delay, minDisplay])

  return visible
}
